import { create } from 'zustand';
import type { TourStep } from '@/components/tour/tourTypes';
import { usePreferencesStore } from '@/stores/preferencesStore';

/**
 * ============================================================================
 * GUIDED TOUR — `data-tour` ATTRIBUTE CONVENTION
 * ============================================================================
 * The guided product tour spotlights real DOM elements. Prompts 2 & 3 mark
 * those elements by adding a `data-tour="<id>"` attribute, e.g.:
 *
 *     <button data-tour="generate-button">Generate</button>
 *     <aside data-tour="platform-sidebar"> ... </aside>
 *
 * Each tour step (a driver.js `DriveStep`) then targets an element via the
 * matching CSS attribute selector:
 *
 *     { element: '[data-tour="generate-button"]', popover: { ... } }
 *
 * The `data-tour` value is a stable, kebab-case identifier — it must NOT change
 * once a tour step references it. Use `data-tour` (never an `id` or a class) so
 * tour targeting stays decoupled from styling and from other tooling.
 *
 * Step arrays live on `currentSteps` in this store. Prompt 2 populates them
 * per phase via `setSteps()`; in this foundation prompt `currentSteps` is `[]`.
 * ============================================================================
 */

/**
 * Tour phases. `null` means no tour is configured / running.
 *
 * ONE continuous chain, ordered the way a first-time user meets the product:
 * build the workspace → walk the app → THEN write a post. Generation is the
 * FINALE, so nothing is left unseen if the user stops once they have posts.
 *
 *   /onboarding   onboarding → onboarding_post → onboarding_region →
 *                 onboarding_services → onboarding_voice →
 *                 onboarding_examples → onboarding_facts →
 *                 onboarding_personas
 *                 (Finish Setup — the app itself navigates to /tool)
 *   /tool         tool_orientation      "you're in — quick lap first"
 *   settings      deep_dive_workspaces_{profile,services,voice,personas}
 *                 — four sub-tours, one per tab of the workspace edit modal,
 *                   one beat each: where what you just entered now LIVES
 *   settings      deep_dive_connections — hooked up BEFORE generating, so the
 *                                         finale's "post it" button is real
 *   /posts        deep_dive_myposts     ┐
 *   /collections  deep_dive_collections ├ the output side (empty for a new
 *   /dashboard    deep_dive_dashboard   ┘ user — the copy says "will land here")
 *   settings      deep_dive_subscription, deep_dive_preferences
 *   /feed         deep_dive_feed        the daily front door; its "write your
 *                                       own" link walks them into the tool
 *   /tool         tool_intro            the controls, ending at GENERATE
 *   /tool         tool_intro_post       variations → psychology → save → post,
 *                                       then the EndScreen. THE END.
 *
 * The onboarding and tool tours are each split into a pre/post pair because
 * their key elements only mount after a user action (analyze / generate). The
 * "post" phase re-fires automatically once that DOM exists.
 *
 * Transitions: where a real nav control exists the user WALKS (a gated step
 * highlights it and waits for the click); where none does, the tour navigates.
 * See `chainTo` / `handOff` in tourSteps.ts.
 */
export type TourPhase =
  | 'onboarding'
  | 'onboarding_post'
  | 'onboarding_region'
  | 'onboarding_services'
  | 'onboarding_voice'
  | 'onboarding_examples'
  | 'onboarding_facts'
  | 'onboarding_personas'
  | 'tool_orientation'
  | 'tool_intro'
  | 'tool_intro_post'
  | 'deep_dive_workspaces_page'
  | 'deep_dive_workspaces_profile'
  | 'deep_dive_workspaces_services'
  | 'deep_dive_workspaces_voice'
  | 'deep_dive_workspaces_personas'
  | 'deep_dive_connections'
  | 'deep_dive_myposts'
  | 'deep_dive_collections'
  | 'deep_dive_dashboard'
  | 'deep_dive_subscription'
  | 'deep_dive_preferences'
  | 'deep_dive_feed'
  | 'deep_dive_case_studies'
  | null;

interface TourState {
  isActive: boolean;
  currentPhase: TourPhase;
  currentStep: number;
  /** Tour steps for the active phase. Empty until a page populates it via setSteps. */
  currentSteps: TourStep[];
  showWelcomeModal: boolean;
  showCheckpointModal: boolean;
  showCloseConfirmModal: boolean;
  /** Final completion modal, shown after the deep_dive_preferences sub-tour. */
  showEndScreen: boolean;
  /**
   * Phase the WelcomeModal should start when the user accepts the tour.
   * Set by the page that opens the modal (onboarding vs tool_intro).
   */
  pendingPhase: TourPhase;
  /**
   * The last tour phase that ended (completed or naturally finished). Used by
   * the /tool page to detect a fresh onboarding→tool_intro handoff so it can
   * auto-start the tool tour without showing the WelcomeModal again.
   */
  lastCompletedPhase: TourPhase;

  startTour: (phase: TourPhase) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  endTour: (markComplete: boolean) => Promise<void>;
  setPhase: (phase: TourPhase) => void;
  /** Replace the step list for the current phase (used by Prompt 2). */
  setSteps: (steps: TourStep[]) => void;
  /** Set which phase the WelcomeModal will start on acceptance. */
  setPendingPhase: (phase: TourPhase) => void;
  /**
   * Re-arm the CURRENT phase from the top. Used by the paused bar's "resume".
   *
   * A paused tour whose phase lives on this same page (the workspace sub-tours
   * run inside a modal) had no way back: the resume button only appeared when the
   * phase had a route to navigate to, so closing the workspace modal mid-tour left
   * the user staring at "tutorial paused" with nothing but "skip tour". This ends
   * the phase and puts it back in `pendingPhase`, so the page's own trigger fires
   * again — re-opening the modal, selecting the tab, and restarting the chapter.
   *
   * `restartNonce` changes on every call so a page's "already fired this phase"
   * guard can tell a genuine restart from a duplicate start.
   */
  restartPhase: () => void;
  /** Bumped by `restartPhase`; page triggers key their fired-guard on it. */
  restartNonce: number;
  /**
   * End the spotlight tour and open the CheckpointModal. Called from the final
   * tool_intro step. Tears down the spotlight (isActive → false) but
   * does NOT persist completion — that happens via the checkpoint buttons.
   */
  triggerCheckpoint: () => void;
  openWelcomeModal: () => void;
  closeWelcomeModal: () => void;
  openCheckpointModal: () => void;
  closeCheckpointModal: () => void;
  openCloseConfirmModal: () => void;
  closeCloseConfirmModal: () => void;
  /** Open the final completion modal; also tears down any active spotlight. */
  openEndScreen: () => void;
  closeEndScreen: () => void;
  /**
   * Reset the tour to a fully fresh state AND clear the server-side completion
   * flags (`has_completed_tutorial` + `tutorial_dismissed_at`) so the whole
   * tour can be replayed. Used by the "Show me the tour again" button.
   */
  reset: () => Promise<void>;
}

export const useTourStore = create<TourState>((set, get) => ({
  isActive: false,
  currentPhase: null,
  currentStep: 0,
  currentSteps: [],
  showWelcomeModal: false,
  showCheckpointModal: false,
  showCloseConfirmModal: false,
  showEndScreen: false,
  pendingPhase: null,
  lastCompletedPhase: null,

  // Begin a tour phase from the first step. Closes any open tour modal.
  //
  // Clears `pendingPhase`: it is a REQUEST to start a phase, and once that phase
  // is running the request is spent. Leaving it set meant a stale 'onboarding'
  // sat there for the rest of the session, and any code that treats a non-null
  // pendingPhase as "a chain handoff is in flight" (Tool.tsx's cold-start guard)
  // would wrongly bail — which is why the tool tour only appeared after a manual
  // refresh. Callers no longer need to null it out by hand after startTour.
  startTour: (phase) =>
    set({
      isActive: true,
      currentPhase: phase,
      currentStep: 0,
      pendingPhase: null,
      showWelcomeModal: false,
      showCheckpointModal: false,
      showCloseConfirmModal: false,
    }),

  nextStep: () => set((s) => ({ currentStep: s.currentStep + 1 })),

  prevStep: () => set((s) => ({ currentStep: Math.max(0, s.currentStep - 1) })),

  goToStep: (step) => set({ currentStep: Math.max(0, step) }),

  /**
   * End the tour and reset all transient tour state.
   * - markComplete=true: persist completion to user preferences (cross-device).
   * - markComplete=false: reset state only, no persistence.
   */
  endTour: async (markComplete) => {
    // Record the phase that just ended so the next page can detect the handoff.
    const endedPhase = get().currentPhase;
    set({
      isActive: false,
      currentPhase: null,
      currentStep: 0,
      currentSteps: [],
      showWelcomeModal: false,
      showCheckpointModal: false,
      showCloseConfirmModal: false,
      showEndScreen: false,
      lastCompletedPhase: endedPhase ?? get().lastCompletedPhase,
    });

    if (markComplete) {
      try {
        await usePreferencesStore.getState().updatePreferences({
          has_completed_tutorial: true,
          tutorial_dismissed_at: new Date().toISOString(),
        });
      } catch (err) {
        // Persistence failure shouldn't block the UI — the tour is already closed.
        console.error('Failed to persist tutorial completion:', err);
      }
    }
  },

  setPhase: (phase) => set({ currentPhase: phase }),

  setSteps: (steps) => set({ currentSteps: steps }),

  setPendingPhase: (phase) => set({ pendingPhase: phase }),

  restartNonce: 0,

  restartPhase: () => {
    const phase = get().currentPhase;
    if (!phase) return;
    set((s) => ({
      isActive: false,
      currentPhase: null,
      currentStep: 0,
      currentSteps: [],
      pendingPhase: phase,
      restartNonce: s.restartNonce + 1,
    }));
  },

  triggerCheckpoint: () =>
    set({
      // Tear down the spotlight (TourProvider unmounts the overlay on
      // isActive→false) and surface the checkpoint fork. Completion is persisted by the
      // CheckpointModal buttons, not here.
      isActive: false,
      showCheckpointModal: true,
      lastCompletedPhase: 'tool_intro',
    }),

  openWelcomeModal: () => set({ showWelcomeModal: true }),
  closeWelcomeModal: () => set({ showWelcomeModal: false }),
  openCheckpointModal: () => set({ showCheckpointModal: true }),
  closeCheckpointModal: () => set({ showCheckpointModal: false }),
  openCloseConfirmModal: () => set({ showCloseConfirmModal: true }),
  closeCloseConfirmModal: () => set({ showCloseConfirmModal: false }),

  openEndScreen: () =>
    set({
      // Tear down any lingering spotlight and surface the completion modal.
      isActive: false,
      currentPhase: null,
      currentStep: 0,
      currentSteps: [],
      showEndScreen: true,
    }),
  closeEndScreen: () => set({ showEndScreen: false }),

  reset: async () => {
    set({
      isActive: false,
      currentPhase: null,
      currentStep: 0,
      currentSteps: [],
      showWelcomeModal: false,
      showCheckpointModal: false,
      showCloseConfirmModal: false,
      showEndScreen: false,
      pendingPhase: null,
      lastCompletedPhase: null,
    });
    try {
      // Clear both server-side flags so the tour will replay. The backend's
      // update_preferences() was updated (Phase 3b) to allow an explicit null
      // for tutorial_dismissed_at — see questions.md.
      await usePreferencesStore.getState().updatePreferences({
        has_completed_tutorial: false,
        tutorial_dismissed_at: null,
      });
    } catch (err) {
      console.error('Failed to reset tutorial state:', err);
    }
  },
}));
